import classNames from "classnames"
import { observer } from "mobx-react"
import React from "react"
import { FeedbackBranch, FeedbackStoreType } from "../models/feedback-store"
import styles from "./Feedback.module.scss"
import { MY_BRANCH_TITLE, branchTitle, formatShortDate, isOwnBranch, shortName } from "./format"

/**
 * Список веток для админа: чат администраторов, его личные обращения,
 * дальше обращения сотрудников по свежести
 */
export const FeedbackBranches = observer(({ store, userId }: FeedbackBranchesProps) => {
  const admins = store.branches.filter((b) => b.kind === "admins")
  const own = store.branches.find((b) => isOwnBranch(b, userId)) || null
  const employees = store.branches
    .filter((b) => b.kind === "employee" && b !== own)
    .sort((a, b) => (b.lastMessageAt || "").localeCompare(a.lastMessageAt || ""))

  // в ветке нет id автора последнего сообщения — своё узнаём по полному ФИО, которое Rost сам передаёт
  // в versionControl (в сессии фронта ФИО сокращённое и для сравнения не годится)
  const previewAuthor = (branch: FeedbackBranch) => {
    const name = branch.lastMessageAuthorName || ""

    if (branch.lastMessageDirection === "in" && name && name === store.viewerName) {
      return "Я"
    }

    return shortName(name)
  }

  const renderBranch = (branch: FeedbackBranch) => (
    <div
      key={branch.id}
      className={classNames(styles.branch, {
        [styles.branchActive]: branch.id === store.activeBranchId,
        [styles.branchAdmins]: branch.kind === "admins",
        [styles.branchOwn]: branch === own,
      })}
      onClick={() => branch.id !== store.activeBranchId && store.openBranch(branch.id)}
    >
      <div className={styles.branchHead}>
        <div className={styles.branchTitle}>{branchTitle(branch, userId)}</div>
        <div className={styles.branchDate}>{formatShortDate(branch.lastMessageAt)}</div>
      </div>

      {branch.kind === "employee" && branch !== own && branch.subjectPosition && (
        <div className={styles.branchPosition}>{branch.subjectPosition}</div>
      )}

      <div className={styles.branchHead}>
        <div className={styles.branchPreview}>
          {branch.lastMessagePreview
            ? `${previewAuthor(branch) ? `${previewAuthor(branch)}: ` : ""}${branch.lastMessagePreview}`
            : "Сообщений нет"}
        </div>
        {branch.unreadCount > 0 && <span className={styles.counter}>{branch.unreadCount}</span>}
      </div>
    </div>
  )

  return (
    <div className={styles.branches}>
      {admins.map(renderBranch)}

      {own ? (
        renderBranch(own)
      ) : (
        // личной ветки ещё нет: первое сообщение без branchId заведёт её
        <div
          className={classNames(styles.branch, styles.branchOwn, {
            [styles.branchActive]: store.activeBranchId === null,
          })}
          onClick={() => store.activeBranchId !== null && store.openBranch(null)}
        >
          <div className={styles.branchTitle}>{MY_BRANCH_TITLE}</div>
          <div className={styles.branchPreview}>Обращений пока нет</div>
        </div>
      )}

      {employees.length > 0 && <div className={styles.branchesGroup}>Обращения сотрудников</div>}
      {employees.map(renderBranch)}
    </div>
  )
})

interface FeedbackBranchesProps {
  store: FeedbackStoreType
  userId: string
}
